import React, { useState, useEffect } from 'react';
import AudioUploader from './AudioUploader';
import AnalysisResults from './AnalysisResults';
import HistorySidebar from './HistorySidebar';
import MixerModal from './MixerModal';
import VideoExportModal from './VideoExportModal';
import { analyzeAudioWithGemini } from '../services/geminiService';
import SafeIcon from '../common/SafeIcon';
import * as MdIcons from 'react-icons/md';

const { MdHistory, MdAdd, MdAutoAwesome, MdAnalytics, MdTitle, MdDescription, MdLabel, MdImage, MdInfoOutline, MdQueueMusic, MdCallMerge, MdOndemandVideo } = MdIcons;

const FeatureItem = ({ icon, colorClass, title, desc }) => (
  <div className="flex gap-4 p-5 rounded-2xl bg-white dark:bg-surface-dark border border-slate-200 dark:border-border-dark/50 hover:border-primary/50 transition-colors">
    <div className={`w-12 h-12 rounded-xl ${colorClass} bg-opacity-10 flex items-center justify-center shrink-0`}>
      <SafeIcon icon={icon} className={`${colorClass.replace('bg-', 'text-')} text-xl`} />
    </div>
    <div>
      <h4 className="font-bold text-base mb-1 text-slate-900 dark:text-white">{title}</h4>
      <p className="text-sm text-slate-500 dark:text-text-secondary">{desc}</p>
    </div>
  </div>
);

const MusicAnalyzer = () => {
  const [audioFile, setAudioFile] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  
  const [showHistory, setShowHistory] = useState(false);
  const [showMixer, setShowMixer] = useState(false);
  const [showVideoExporter, setShowVideoExporter] = useState(false);
  
  const [history, setHistory] = useState(() => {
    const saved = localStorage.getItem('analysis_history');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('analysis_history', JSON.stringify(history));
  }, [history]);

  const addToHistory = (file, results) => {
    const newItem = {
      id: Date.now(),
      fileName: file.name,
      timestamp: new Date().toISOString(),
      results: results
    };
    setHistory(prev => [newItem, ...prev]);
  };

  const handleFileUpload = (file) => {
    setAudioFile(file);
    setAnalysisResults(null);
    setError(null);
  };

  const handleAnalyze = async () => {
    if (!audioFile) return;
    setIsAnalyzing(true);
    setError(null);

    try {
      const results = await analyzeAudioWithGemini(audioFile);
      setAnalysisResults(results);
      addToHistory(audioFile, results);
    } catch (err) {
      setError(err.message || 'Failed to analyze audio.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleHistorySelect = (item) => {
    setAnalysisResults(item.results);
    setAudioFile({ name: item.fileName, size: 0, type: 'audio/mp3', fromHistory: true });
    setShowHistory(false);
    setError(null);
  };

  const handleHistoryDelete = (id) => {
    setHistory(prev => prev.filter(item => item.id !== id));
  };

  const handleClearHistory = () => {
    if (confirm('Are you sure you want to delete all history?')) {
      setHistory([]);
    }
  };

  const handleMixComplete = (mixedFile) => {
    setAudioFile(mixedFile);
    setAnalysisResults(null);
    setError(null);
  };

  return (
    <div className="w-full flex flex-col gap-8">
      <HistorySidebar 
        isOpen={showHistory} 
        onClose={() => setShowHistory(false)} 
        history={history}
        onSelect={handleHistorySelect}
        onDelete={handleHistoryDelete}
        onClearHistory={handleClearHistory}
      />
      
      <MixerModal 
        isOpen={showMixer} 
        onClose={() => setShowMixer(false)}
        initialTrack1={audioFile && !audioFile.fromHistory ? audioFile : null}
        onMixComplete={handleMixComplete}
      />

      <VideoExportModal 
        isOpen={showVideoExporter}
        onClose={() => setShowVideoExporter(false)}
        audioFile={audioFile && !audioFile.fromHistory ? audioFile : null}
      />

      {/* Page Heading */}
      <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-4">
        <div className="flex flex-col gap-2 max-w-2xl">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-bold uppercase tracking-wider">New Project</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black leading-tight tracking-tight text-slate-900 dark:text-white">Audio Intelligence</h1>
          <p className="text-slate-500 dark:text-text-secondary text-base md:text-lg font-normal leading-relaxed">
            Upload your track, mix songs, generate SEO content, and export ready-to-upload YouTube videos.
          </p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={() => setShowHistory(true)}
            className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 dark:border-border-dark hover:border-primary/50 hover:bg-slate-100 dark:hover:bg-surface-dark transition-all text-slate-600 dark:text-white"
          >
            <SafeIcon icon={MdHistory} className="text-xl text-slate-500 dark:text-text-secondary group-hover:text-primary" />
            <span className="font-medium text-sm hidden sm:block">History</span>
          </button>
          
          <button 
            onClick={() => setShowMixer(true)}
            className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 dark:border-border-dark hover:border-primary/50 hover:bg-slate-100 dark:hover:bg-surface-dark transition-all text-slate-600 dark:text-white"
          >
            <SafeIcon icon={MdCallMerge} className="text-xl text-slate-500 dark:text-text-secondary group-hover:text-primary" />
            <span className="font-medium text-sm">Mix Songs</span>
          </button>

          <button 
            onClick={() => setShowVideoExporter(true)}
            disabled={!audioFile || audioFile?.fromHistory}
            title={!audioFile ? "Upload an audio file first" : "Create MP4 Video"}
            className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-red-500"
          >
            <SafeIcon icon={MdOndemandVideo} className="text-xl" />
            <span className="font-bold text-sm">Create Video</span>
          </button>

          <button 
            className="group flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white hover:bg-primary/90 transition-all shadow-lg shadow-primary/25 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleAnalyze}
            disabled={!audioFile || isAnalyzing || audioFile?.fromHistory}
          >
            <SafeIcon icon={isAnalyzing ? MdAutoAwesome : MdAdd} className={`text-xl ${isAnalyzing ? 'animate-spin' : ''}`} />
            <span className="font-bold text-sm">
              {isAnalyzing ? 'Processing...' : 'Generate SEO'}
            </span>
          </button>
        </div>
      </div>

      {error && (
        <div className="w-full p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-center font-medium">
          {error}
        </div>
      )}

      {/* Content Grid */}
      <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: Upload Area (Dominant) */}
        <div className="lg:col-span-7 flex flex-col gap-8">
          <AudioUploader onFileUpload={handleFileUpload} audioFile={audioFile} />
          
          {/* Features Grid */}
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3 px-2">
              <SafeIcon icon={MdAutoAwesome} className="text-primary text-xl" />
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Generated Outputs</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FeatureItem 
                icon={MdAnalytics} colorClass="bg-blue-500" 
                title="Deep Analysis" desc="Detailed breakdown of mood, tempo, and content structure." 
              />
              <FeatureItem 
                icon={MdTitle} colorClass="bg-purple-500" 
                title="SEO Titles" desc="Click-worthy, optimized titles for higher CTR." 
              />
              <FeatureItem 
                icon={MdDescription} colorClass="bg-pink-500" 
                title="Description" desc="Full description with chapters and trending hashtags." 
              />
              <FeatureItem 
                icon={MdLabel} colorClass="bg-emerald-500" 
                title="Smart Tags" desc="15-20 high-impact tags for maximum discoverability." 
              />
              <FeatureItem 
                icon={MdOndemandVideo} colorClass="bg-red-500" 
                title="MP4 Video Export" desc="Combine artwork and track into a YouTube-ready video file." 
              />
              <FeatureItem 
                icon={MdQueueMusic} colorClass="bg-indigo-500" 
                title="Suno AI v5 Style" desc="Style prompts to recreate the song's vibe in Suno AI." 
              />
            </div>
          </div>
        </div>

        {/* Right Column: Results Sidebar */}
        <div className="lg:col-span-5 flex flex-col h-full">
          <div className="h-full min-h-[500px] flex flex-col rounded-2xl border border-slate-200 dark:border-border-dark bg-white dark:bg-background-dark relative overflow-hidden shadow-sm">
            <div className="p-6 border-b border-slate-200 dark:border-border-dark flex justify-between items-center bg-slate-50 dark:bg-surface-dark/50">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white">Results</h3>
              <div className={`px-2 py-1 rounded text-xs font-mono font-bold ${analysisResults ? 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400' : 'bg-slate-200 text-slate-500 dark:bg-border-dark dark:text-slate-400'}`}>
                {isAnalyzing ? 'PROCESSING' : analysisResults ? 'READY' : 'IDLE'}
              </div>
            </div>
            
            <div className="flex-1 relative overflow-hidden">
              <AnalysisResults results={analysisResults} isAnalyzing={isAnalyzing} />
            </div>

            <div className="p-4 bg-slate-50 dark:bg-surface-dark/30 border-t border-slate-200 dark:border-border-dark mt-auto z-10">
              <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-text-secondary">
                <SafeIcon icon={MdInfoOutline} className="text-lg" />
                <span>Processing usually takes ~30 seconds</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MusicAnalyzer;